import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Container, FormField, Icon, Input, Text } from '@legalshield/adonis-ux-framework';

export type DropDownOption = {
  title: string;
  value: string;
};

type Props = {
  label: string;
  required?: boolean;
  multipleChoice?: boolean;
  multiSize?: 'normal' | 'small';
  options: DropDownOption[];
  value: DropDownOption[];
  onChange: (next: DropDownOption[]) => void;
  disabled?: boolean;
};

const compareByTitle = (a: DropDownOption, b: DropDownOption) => a.title.localeCompare(b.title);

const SearchWithDropDown = ({
  label,
  required,
  multipleChoice = false,
  multiSize = 'normal',
  options,
  value,
  onChange,
  disabled,
}: Props) => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState<string>(multipleChoice ? '' : value[0]?.title ?? '');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!multipleChoice) {
      setSearchText(value[0]?.title ?? '');
    }
  }, [value, multipleChoice]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleOutsideClick, true);
    return () => document.removeEventListener('click', handleOutsideClick, true);
  }, []);

  const selectedValues = new Set(value.map((v) => v.value));
  const visibleOptions = options
    .filter((opt) => !selectedValues.has(opt.value))
    .filter((opt) => opt.title.toLowerCase().includes(searchText.toLowerCase()))
    .sort(compareByTitle);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!showDropdown) setShowDropdown(true);
    if (!multipleChoice && e.target.value !== value[0]?.title) {
      onChange([]);
    }
    setSearchText(e.target.value);
  };

  const handlePickOption = (option: DropDownOption) => {
    if (multipleChoice) {
      onChange([...value, option].sort(compareByTitle));
      setSearchText('');
      setShowDropdown(false);
    } else {
      onChange([option]);
      setSearchText(option.title);
      setShowDropdown(false);
    }
  };

  const handleRemoveSelected = (option: DropDownOption) => {
    onChange(value.filter((v) => v.value !== option.value));
  };

  return (
    <div ref={formRef}>
      <FormField id={`swd-${label}`} label={label} required={required} noMargin>
        <>
          <Input
            type="search"
            value={searchText}
            onChange={handleSearchChange}
            onClick={() => !disabled && setShowDropdown(true)}
            disabled={disabled}
          />
          {showDropdown && visibleOptions.length > 0 ? (
            <Card padding={3} classNames={['elevation-2', 'mt-3', 'rnq-swd-card']}>
              {visibleOptions.map((option) => (
                <div
                  key={option.value}
                  className="rnq-swd-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePickOption(option)}
                  onKeyUp={(e) => e.key === 'Enter' && handlePickOption(option)}
                >
                  <Text as="span" textSize="extra-large" text={option.title} />
                </div>
              ))}
            </Card>
          ) : null}
        </>
      </FormField>

      {multipleChoice && value.length > 0 && (
        <>
          <Container classNames={['mt-4']}>
            <Text as="p" text={`Selected ${label}`} bold />
          </Container>
          <Container flexbox flexWrap="wrap" classNames={['mb-3']}>
            {value.map((option) =>
              multiSize === 'small' ? (
                <Container
                  key={option.value}
                  flexbox
                  classNames={['mt-4', 'rnq-swd-chip', 'mr-3']}
                  onClick={() => handleRemoveSelected(option)}
                >
                  <Text as="p" textSize="medium" text={option.title} classNames={['px-2']} />
                  <Icon name="feedback_close" size="small" />
                </Container>
              ) : (
                <Button
                  key={option.value}
                  label={option.title}
                  variant="standard"
                  iconRight="menu_close_md"
                  classNames={['mr-4', 'mt-4']}
                  onClick={() => handleRemoveSelected(option)}
                />
              ),
            )}
          </Container>
        </>
      )}
    </div>
  );
};

export default SearchWithDropDown;
